import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, Globe, MapPin, 
  Loader2, Save, Trash2, ShieldCheck, AlertCircle, Navigation
} from 'lucide-react';
import { settingsService } from '../../services/settingsService';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('office');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Form States
  const [officeData, setOfficeData] = useState({ 
    latitude: 0, 
    longitude: 0, 
    radius: 100, 
    office_name: '',
    location_check_enabled: true // Toggle for Geo-fencing
  });
  const [ipData, setIpData] = useState({ allowed_ips: [], ddns_hostname: '', ip_check_enabled: true });
  const [newIp, setNewIp] = useState('');

  const loadSettings = async () => {
    setLoading(true);
    try {
      if (activeTab === 'office') {
        const res = await settingsService.getOfficeSettings();
        if (res.data) setOfficeData(res.data);
      } else if (activeTab === 'ip') {
        const res = await settingsService.getIPSettings();
        setIpData(res.data || { allowed_ips: [], ip_check_enabled: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, [activeTab]);

  // browser se current location fetch karne ka function
  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition((position) => {
      setOfficeData({
        ...officeData,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
    }, (err) => {
      alert("Error fetching location: " + err.message);
    });
  };

  const handleOfficeSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsService.saveOfficeSettings(officeData);
      setMessage({ type: 'success', text: 'Office & Geo-fencing settings saved!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleIpSave = async () => {
    setSaving(true);
    try {
      await settingsService.saveIPSettings(ipData);
      setMessage({ type: 'success', text: 'IP security settings updated!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const addIp = () => {
    if (!newIp) return;
    setIpData({ ...ipData, allowed_ips: [...ipData.allowed_ips, newIp] });
    setNewIp('');
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            <SettingsIcon className="w-7 h-7 text-primary" /> System Settings
          </h1>
          <p className="text-sm text-gray-500 font-medium">Configure security boundaries and access rules</p>
        </div>
      </div>

      {/* Modern Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-100 overflow-x-auto no-scrollbar">
        <TabBtn active={activeTab === 'office'} onClick={() => setActiveTab('office')} icon={<MapPin className="w-4 h-4" />} label="Geo-Fencing" />
        <TabBtn active={activeTab === 'ip'} onClick={() => setActiveTab('ip')} icon={<Globe className="w-4 h-4" />} label="IP Security" />
      </div>

      {message && (
        <div className={'p-4 rounded-2xl text-sm font-bold flex items-center justify-between ' + (message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700')}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <ShieldCheck className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
          <button onClick={() => setMessage(null)} className="text-current opacity-50 hover:opacity-100">×</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-12 h-12" /></div>
      ) : (
        <div className="max-w-4xl space-y-6">
          
          {/* OFFICE & GEO-FENCING TAB */}
          {activeTab === 'office' && (
            <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
              <div className="p-6 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary"><MapPin className="w-5 h-5" /></div>
                  <h2 className="font-bold text-gray-900 uppercase tracking-widest text-xs">Office Boundary</h2>
                </div>
                <Toggle 
                  enabled={officeData.location_check_enabled} 
                  onToggle={() => setOfficeData({...officeData, location_check_enabled: !officeData.location_check_enabled})} 
                  label="Geo-fencing"
                />
              </div>

              <form onSubmit={handleOfficeSave} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Office / Branch Name</label>
                    <input value={officeData.office_name} onChange={(e) => setOfficeData({...officeData, office_name: e.target.value})} className="w-full p-3 border rounded-xl bg-gray-50 outline-none focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all" />
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Latitude</label>
                    <input type="number" step="any" value={officeData.latitude} onChange={(e) => setOfficeData({...officeData, latitude: parseFloat(e.target.value)})} className="w-full p-3 border rounded-xl bg-gray-50 font-mono text-sm" />
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Longitude</label>
                    <input type="number" step="any" value={officeData.longitude} onChange={(e) => setOfficeData({...officeData, longitude: parseFloat(e.target.value)})} className="w-full p-3 border rounded-xl bg-gray-50 font-mono text-sm" />
                  </div>

                  <div className="md:col-span-2">
                    <button type="button" onClick={fetchCurrentLocation} className="flex items-center gap-2 text-primary font-black text-[10px] uppercase tracking-widest hover:underline">
                      <Navigation className="w-3.5 h-3.5" /> Use My Current Location
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Allowed Radius (Meters)</label>
                    <input type="number" value={officeData.radius} onChange={(e) => setOfficeData({...officeData, radius: parseFloat(e.target.value)})} className="w-full p-3 border rounded-xl bg-gray-50" />
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-50">
                  <button type="submit" disabled={saving} className="bg-primary text-white px-10 py-3.5 rounded-2xl text-sm font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all">
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Save Configuration
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* IP WHITELIST TAB */}
          {activeTab === 'ip' && (
            <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
              <div className="p-6 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary"><Globe className="w-5 h-5" /></div>
                  <h2 className="font-bold text-gray-900 uppercase tracking-widest text-xs">IP Access Control</h2>
                </div>
                <Toggle 
                  enabled={ipData.ip_check_enabled} 
                  onToggle={() => setIpData({...ipData, ip_check_enabled: !ipData.ip_check_enabled})} 
                  label="IP Restriction"
                />
              </div>

              <div className="p-8 space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5 tracking-widest">DDNS Hostname (for offices without a static IP)</label>
                  <input
                    value={ipData.ddns_hostname || ''}
                    onChange={(e) => setIpData({ ...ipData, ddns_hostname: e.target.value })}
                    placeholder="e.g. office.synvex.ddns.net"
                    className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 outline-none focus:bg-white font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Your router keeps this hostname pointing at your current public IP — the system checks it live at attendance time instead of a fixed IP.</p>
                </div>

                <div className="flex gap-3">
                  <input value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder="Add fixed IP (e.g. 182.163.x.x)" className="flex-1 p-3 border border-gray-100 rounded-xl bg-gray-50 outline-none focus:bg-white font-mono text-sm" />
                  <button onClick={addIp} className="bg-gray-900 text-white px-6 rounded-xl text-xs font-black uppercase tracking-widest">Add</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ipData.allowed_ips.map((ip, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 group hover:border-primary/30 transition-all">
                      <span className="font-mono text-sm font-bold text-gray-700">{ip}</span>
                      <button onClick={() => setIpData({...ipData, allowed_ips: ipData.allowed_ips.filter((_, i) => i !== idx)})} className="text-red-400 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-50">
                  <button onClick={handleIpSave} disabled={saving} className="bg-primary text-white px-10 py-3.5 rounded-2xl text-sm font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all">
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Update Whitelist
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Internal Sub-Components
const TabBtn = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} className={'px-8 py-4 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ' + (active ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-gray-400 hover:text-gray-600')}>
    {icon} {label}
  </button>
);

const Toggle = ({ enabled, onToggle, label }) => (
  <div className="flex items-center gap-3">
    <span className="text-[10px] font-black uppercase text-gray-400 tracking-tighter">{label}:</span>
    <button onClick={onToggle} className={'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ' + (enabled ? 'bg-green-600' : 'bg-gray-200')}>
      <span className={'inline-block h-4 w-4 transform rounded-full bg-white transition-transform ' + (enabled ? 'translate-x-6' : 'translate-x-1')} />
    </button>
    <span className={'text-[10px] font-black uppercase ' + (enabled ? 'text-green-600' : 'text-gray-400')}>
      {enabled ? 'ON' : 'OFF'}
    </span>
  </div>
);

export default Settings;